import { useState, useEffect, useRef } from "react";
import { toast } from "react-hot-toast";
import FeaturedHotelCard from "../../../components/hotel/FeaturedHotelCard";

const FeaturedHotelsSection = ({ searchParams = {} }) => {
  const [featuredHotels, setFeaturedHotels] = useState([]);
  const [isLoadingHotels, setIsLoadingHotels] = useState(true);
  const [validationError, setValidationError] = useState(null);
  const previousSearchParams = useRef(null);

  useEffect(() => {
    // Function to check if relevant search parameters have changed
    const haveRelevantParamsChanged = () => {
      if (!previousSearchParams.current) return true;

      // Only check the parameters that should trigger a refetch
      const relevantParams = [
        "serviceType",
        "checkInDate",
        "checkOutDate",
        "pets",
      ];

      for (const param of relevantParams) {
        if (param === "pets") {
          // For pets array, check if length changed
          if (
            (searchParams.pets?.length || 0) !==
            (previousSearchParams.current.pets?.length || 0)
          ) {
            return true;
          }

          // Check if any pet details changed
          if (searchParams.pets?.length) {
            for (let i = 0; i < searchParams.pets.length; i++) {
              const currentPet = searchParams.pets[i];
              const previousPet = previousSearchParams.current.pets?.[i];

              if (
                !previousPet ||
                currentPet.species !== previousPet.species ||
                currentPet.weight !== previousPet.weight
              ) {
                return true;
              }
            }
          }
        } else if (
          searchParams[param] !== previousSearchParams.current[param]
        ) {
          return true;
        }
      }

      return false;
    };

    const fetchHotels = async () => {
      // Check if we should update based on parameter changes
      if (!haveRelevantParamsChanged()) {
        setIsLoadingHotels(false);
        return;
      }

      try {
        setIsLoadingHotels(true);
        // Clear any previous validation errors
        setValidationError(null);

        const response = await fetch("/api/hotels/featured");

        if (!response.ok) {
          throw new Error("Failed to fetch featured hotels");
        }

        const result = await response.json();

        // Make sure we have the data array before proceeding
        if (!result.data || !Array.isArray(result.data)) {
          console.error(
            "Invalid data structure in featured hotels response",
            result
          );
          setFeaturedHotels([]);
          setIsLoadingHotels(false);
          return;
        }

        // Check if we have search parameters and pets to check availability
        const hasSearchParams =
          searchParams?.checkInDate &&
          searchParams?.checkOutDate &&
          Array.isArray(searchParams?.pets) &&
          searchParams.pets.length > 0;

        if (hasSearchParams) {
          // Validate dates before proceeding
          const isValid = validateDates(
            searchParams.checkInDate,
            searchParams.checkOutDate
          );

          if (!isValid) {
            // If validation failed, we'll just show the hotels without availability info
            setFeaturedHotels(result.data);
            setIsLoadingHotels(false);
            return;
          }

          // Try to get availability info for each featured hotel
          try {
            // Process all featured hotels asynchronously
            const hotelsWithAvailability = await Promise.all(
              result.data.map(async (hotel) => {
                try {
                  // Create petsByType and minMaxWeights safely
                  const petsByType = {};
                  const minMaxWeights = {};

                  searchParams.pets.forEach((pet) => {
                    const species = pet.species || "Unknown";

                    if (!petsByType[species]) {
                      petsByType[species] = [];
                    }

                    petsByType[species].push({
                      type: species,
                      weight: pet.weight || 0,
                      _id: pet._id || null,
                      name: pet.name || null,
                      petImg: pet.petImg || null,
                      breed: pet.breed || null,
                    });
                  });

                  // Calculate min/max weights
                  Object.keys(petsByType).forEach((species) => {
                    const weights = petsByType[species].map(
                      (pet) => Number(pet.weight) || 0
                    );
                    minMaxWeights[species] = {
                      min: Math.min(...weights),
                      max: Math.max(...weights),
                    };
                  });

                  // Create query params for availability check
                  const queryParams = new URLSearchParams();
                  queryParams.set("serviceType", searchParams.serviceType);
                  queryParams.set("startDate", searchParams.checkInDate);
                  queryParams.set("endDate", searchParams.checkOutDate);
                  queryParams.set(
                    "petData",
                    JSON.stringify({
                      petsByType,
                      minMaxWeights,
                    })
                  );

                  // Use the new availability check routes
                  const availabilityUrl = `/api/hotels/${hotel._id}/checkAvailability?${queryParams}`;

                  const availabilityResponse = await fetch(availabilityUrl);

                  if (availabilityResponse.ok) {
                    const availabilityData = await availabilityResponse.json();

                    if (availabilityData.available) {
                      // Hotel is available with room assignments
                      return {
                        ...hotel,
                        available: true,
                        petRoomAssignments:
                          availabilityData.petRoomAssignments || [],
                        pricePerNight: availabilityData.pricePerNight || 0,
                        totalStayAmount: availabilityData.totalStayAmount,
                        dailyBreakdown: availabilityData.dailyBreakdown,
                        multiPetDiscount: availabilityData.multiPetDiscount
                      };
                    } else {
                      // Not available
                      return {
                        ...hotel,
                        available: false,
                        unavailableReason:
                          availabilityData.reason ||
                          "Hotel is not available for these dates",
                        petRoomAssignments: [],
                        pricePerNight: 0,
                      };
                    }
                  } else {
                    console.error(
                      `Error checking availability for hotel ${hotel._id}:`,
                      availabilityResponse.statusText
                    );
                    return {
                      ...hotel,
                      available: false,
                      unavailableReason: "Error checking availability",
                      petRoomAssignments: [],
                      pricePerNight: 0,
                    };
                  }
                } catch (error) {
                  console.error(
                    `Error checking availability for hotel ${hotel._id}:`,
                    error
                  );
                  return {
                    ...hotel,
                    available: false,
                    unavailableReason: "Error checking availability",
                    petRoomAssignments: [],
                    pricePerNight: 0,
                  };
                }
              })
            );

            setFeaturedHotels(hotelsWithAvailability);
          } catch (error) {
            console.error("Error checking availability:", error);
            // Fall back to displaying hotels without availability info
            setFeaturedHotels(result.data);
          }
        } else {
          // No search params, just show the hotels
          setFeaturedHotels(result.data);
        }

        // Update previous search params ref
        previousSearchParams.current = { ...searchParams };
      } catch (error) {
        console.error("Error fetching hotels:", error);
        toast.error("Failed to load hotels");
        setFeaturedHotels([]);
      } finally {
        setIsLoadingHotels(false);
      }
    };

    fetchHotels();
  }, [searchParams]);

  // Date validation function similar to SearchContainer
  const validateDates = (checkInDate, checkOutDate) => {
    // Convert strings to Date objects
    const checkIn = new Date(checkInDate);
    const checkOut = new Date(checkOutDate);
    const now = new Date();

    const minCheckInDate = new Date(now.getTime());

    // Maximum date is 3 months from minimum check-in
    const maxDate = new Date(minCheckInDate);
    maxDate.setMonth(maxDate.getMonth() + 3);

    if (checkOut <= checkIn) {
      setValidationError("Check-out date must be after Check-in date");
      return false;
    }

    if (checkIn > maxDate || checkOut > maxDate) {
      setValidationError("Dates must be within the next 3 months");
      return false;
    }

    return true;
  };

  return (
    <div className="max-w-6xl mx-auto px-4 mb-16">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Featured Hotels
        </h2>
        {validationError ? (
          <p className="text-orange-600 bg-orange-50 px-4 py-2 rounded-md inline-block">
            <span className="font-medium">Note:</span> {validationError}{" "}
            (showing hotels without availability info)
          </p>
        ) : (
          <p className="text-gray-600">
            Explore the best accommodations for your furry friends.
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {isLoadingHotels
          ? [...Array(4)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-lg shadow-lg p-4 animate-pulse"
              >
                <div className="h-48 bg-gray-200 rounded-lg mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))
          : featuredHotels.map((hotel) => (
              <FeaturedHotelCard
                key={hotel._id}
                hotel={hotel}
                searchParams={searchParams}
              />
            ))}
      </div>

      {/* No Results State */}
      {!isLoadingHotels && featuredHotels.length === 0 && (
        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-200 mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            No Featured Hotels Found
          </h3>
          <p className="text-gray-500">
            We couldn't find any featured hotels that match your search
            criteria.
          </p>
        </div>
      )}
    </div>
  );
};

export default FeaturedHotelsSection;
